import type { ApiErrorBody, Attachment, StandingAction } from '@spawnwire/shared';
import { apiUrl, getAuthToken, isNative } from './platform';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

let onBanned: ((ban: StandingAction) => void) | null = null;
/** Called when the server says the account is banned, with the ban to show. */
export function setBannedHandler(fn: (ban: StandingAction) => void): void {
  onBanned = fn;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    /* not JSON */
  }
  const message = body?.error?.message ?? (res.status === 0 ? 'Network error' : `Request failed (${res.status})`);
  return new ApiError(res.status, body?.error?.code ?? 'error', message, body?.error?.details);
}

/** The Android app signs requests with its session token; the website relies on the httpOnly cookie. */
function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

export async function api<T = unknown>(method: Method, path: string, body?: unknown, opts: { signal?: AbortSignal; headers?: Record<string, string> } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(`/api${path}`), {
      method,
      // The app never sends cookies to the API (it is on another origin there).
      credentials: isNative ? 'omit' : 'same-origin',
      headers: {
        ...authHeaders(),
        ...(method !== 'GET' ? { 'x-spawnwire-csrf': '1' } : {}),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...opts.headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.');
  }
  if (res.status === 401 && path !== '/auth/login' && path !== '/auth/session') onUnauthorized?.();
  if (!res.ok) {
    const err = await parseError(res);
    const ban = (err.details as { ban?: StandingAction } | undefined)?.ban;
    if (err.code === 'account_banned' && ban) onBanned?.(ban);
    throw err;
  }
  if (res.status === 204) return undefined as T;
  // Some answers (202 Accepted) carry no body at all.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const http = {
  get: <T>(path: string, opts?: { signal?: AbortSignal }) => api<T>('GET', path, undefined, opts),
  post: <T>(path: string, body: unknown = {}) => api<T>('POST', path, body),
  patch: <T>(path: string, body: unknown = {}) => api<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown = {}) => api<T>('PUT', path, body),
  del: <T>(path: string, body?: unknown) => api<T>('DELETE', path, body),
};

export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

/** Multipart upload through XHR so we can report progress (fetch cannot yet). */
export function uploadFile<T = Attachment>(path: string, file: File, onProgress?: (fraction: number) => void): UploadHandle<T> {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', apiUrl(`/api${path}`));
    xhr.withCredentials = !isNative;
    for (const [name, value] of Object.entries(authHeaders())) xhr.setRequestHeader(name, value);
    xhr.setRequestHeader('x-spawnwire-csrf', '1');
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve(xhr.response as T);
      const body = xhr.response as Partial<ApiErrorBody> | null;
      reject(new ApiError(xhr.status, body?.error?.code ?? 'error', body?.error?.message ?? `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'The upload failed. Check your connection.'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Upload cancelled.'));
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
